// Context Pack Generator — public API (ARCHITECTURE.md §9).
//
// `generateContextPack` is the entry point: given a DependencyGraph and a set
// of changed files, it runs impact analysis, ranks the affected files by
// relevance, reads their source from disk, selects as many as fit the token
// budget, and assembles a ContextPack for a coding agent. The changed files
// themselves enter the ranking at depth 0 so they are always present and
// rendered first.

import { readFile } from "node:fs/promises";
import { join } from "node:path";

import type { SymbolInfo } from "../analyzer/types.js";
import type { EdgeClassification } from "../types.js";
import type { EvidenceType } from "../evidence/types.js";
import type { DependencyGraph } from "../dependency/types.js";
import type { AffectedEntry, ImpactReport } from "../impact/types.js";
import { analyzeImpact } from "../impact/index.js";
import type { ContextPack } from "./types.js";
import { rankFiles, type RankedFile } from "./ranker.js";
import { selectWithinBudget } from "./budget.js";
import { assembleContextPack } from "./assembler.js";

export * from "./types.js";
export * from "./ranker.js";
export * from "./budget.js";
export * from "./assembler.js";

/** Default token budget for a context pack. */
export const DEFAULT_BUDGET_TOKENS = 50_000;

/**
 * Context granularity (§6):
 *   "file"   — render whole files / line heuristics by relevance (default).
 *   "symbol" — render only the line ranges of affected symbols (symbol-scoped
 *              sections), driven by the impact analysis symbol results.
 */
export const CONTEXT_GRANULARITIES = ["file", "symbol"] as const;

export type ContextGranularity = (typeof CONTEXT_GRANULARITIES)[number];

export interface GenerateContextPackOptions {
  graph: DependencyGraph;
  /** The changed files to build context around. */
  changed: string[];
  /** Token budget for the pack; default: 50,000. */
  budgetTokens?: number;
  /**
   * Edge classifications to traverse during impact analysis; default:
   * deterministic + candidate. Restrict to `["deterministic"]` to skip
   * candidate edges (Issue #21).
   */
  edgeTypes?: EdgeClassification[];
  /** Evidence types to exclude when scoring relevance (Issue #21). */
  evidenceExclude?: EvidenceType[];
  /** Cap the number of ranked files considered for the pack. */
  limit?: number;
  /** @deprecated Issue #21 — use `edgeTypes`. Honoured for backward compat. */
  minConfidence?: number;
  taskId?: string;
  /** filePath → artifact IDs; defaults to `graph.artifactMapping`. */
  artifactMapping?: Map<string, string[]>;
  /** Filesystem root the file paths are resolved against. */
  projectRoot: string;
  /** Context granularity; default: "symbol" (symbol-scoped sections). */
  granularity?: ContextGranularity;
  /** Pre-loaded file contents to avoid re-reading from disk. */
  fileContents?: Map<string, string>;
  /** Optional agent-contracts Navigation Index for the contract overlay (§8.4). */
  navigationIndex?: Record<string, unknown>;
}

/**
 * Collect the affected symbols per file for symbol-scoped rendering (§6.1).
 * Sources:
 *   - changed seeds that are symbol ids (filePath#name) → that symbol;
 *   - changed seeds that are file paths → every symbol the file declares;
 *   - impact-report entries carrying a `symbolId` → that symbol.
 * Symbols are resolved against the AST graph so each carries its full line
 * range; unknown ids are skipped. Returns an empty map when the graph has no
 * symbol nodes (degrades gracefully to file-level rendering).
 */
function collectAffectedSymbols(
  graph: DependencyGraph,
  changed: string[],
  report: ImpactReport,
): Map<string, SymbolInfo[]> {
  const byFile = new Map<string, SymbolInfo[]>();
  const seen = new Set<string>();

  const addSymbol = (symbolId: string): void => {
    if (seen.has(symbolId)) return;
    const node = graph.ast.nodes.get(symbolId);
    const info = node?.kind === "symbol" ? node.symbolInfo : undefined;
    if (!node || !info) return;
    seen.add(symbolId);
    const list = byFile.get(node.filePath);
    if (list) list.push(info);
    else byFile.set(node.filePath, [info]);
  };

  const addFile = (filePath: string): void => {
    for (const node of graph.ast.nodes.values()) {
      if (
        node.kind === "symbol" &&
        node.filePath === filePath &&
        node.symbolInfo
      ) {
        addSymbol(node.symbolInfo.symbolId);
      }
    }
  };

  for (const seed of changed) {
    if (seed.includes("#")) addSymbol(seed);
    else addFile(seed);
  }
  for (const entry of [
    ...report.directlyAffected,
    ...report.transitivelyAffected,
  ]) {
    if (entry.symbolId) addSymbol(entry.symbolId);
  }

  return byFile;
}

/**
 * Count the graph edges incident to each file (degree). Higher connectivity
 * feeds the ranker's edge-count factor (§9.2).
 */
function computeEdgeCounts(graph: DependencyGraph): Map<string, number> {
  const counts = new Map<string, number>();
  const bump = (id: string): void => {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  };
  for (const edge of graph.edges) {
    bump(edge.from);
    bump(edge.to);
  }
  return counts;
}

/**
 * Generate a token-budget-constrained ContextPack for a set of changed files
 * (ARCHITECTURE.md §9).
 */
export async function generateContextPack(
  options: GenerateContextPackOptions,
): Promise<ContextPack> {
  const {
    graph,
    changed,
    budgetTokens = DEFAULT_BUDGET_TOKENS,
    edgeTypes,
    evidenceExclude,
    limit,
    minConfidence,
    taskId,
    artifactMapping = graph.artifactMapping,
    projectRoot,
    granularity = "symbol",
    fileContents: preloadedContents,
    navigationIndex,
  } = options;

  // 1. Impact analysis — what else is affected downstream of the change?
  const report = await analyzeImpact({
    graph,
    changed,
    direction: "downstream",
    ...(edgeTypes ? { edgeTypes } : {}),
    ...(evidenceExclude ? { evidenceExclude } : {}),
    ...(minConfidence !== undefined ? { minConfidence } : {}),
    artifactMapping,
    ...(navigationIndex ? { navigationIndex } : {}),
  });

  // 2. Seed the ranking with the changed files at depth 0, then the affected
  //    entries. Dedupe on path, preferring the shallowest (changed) entry.
  const seen = new Set<string>();
  const affectedEntries: AffectedEntry[] = [];
  for (const path of changed) {
    if (seen.has(path)) continue;
    seen.add(path);
    const artifactIds = artifactMapping.get(path);
    affectedEntries.push({
      path,
      ...(artifactIds && artifactIds.length > 0
        ? { artifactId: artifactIds[0] }
        : {}),
      depth: 0,
      evidence: [],
    });
  }
  for (const entry of [
    ...report.directlyAffected,
    ...report.transitivelyAffected,
  ]) {
    if (seen.has(entry.path)) continue;
    seen.add(entry.path);
    affectedEntries.push(entry);
  }

  // 3. Rank by relevance, then cap with `limit` (the changed files rank highest
  //    via the depth factor, so they survive the cut).
  const edgeCounts = computeEdgeCounts(graph);
  const rankedAll: RankedFile[] = rankFiles({
    affectedEntries,
    artifactMapping,
    edgeCounts,
    ...(evidenceExclude ? { evidenceExclude } : {}),
  });
  const ranked =
    limit !== undefined && limit >= 0
      ? rankedAll.slice(0, limit)
      : rankedAll;

  // 4. Read file contents (use pre-loaded map when provided; read only missing).
  const fileContents = new Map<string, string>(preloadedContents ?? []);
  await Promise.all(
    ranked.map(async (file) => {
      if (fileContents.has(file.path)) return;
      try {
        const content = await readFile(join(projectRoot, file.path), "utf8");
        fileContents.set(file.path, content);
      } catch {
        // File unreadable (deleted, outside root); rendered as reference.
        fileContents.set(file.path, "");
      }
    }),
  );

  // 5. Select within budget.
  const selection = selectWithinBudget({ ranked, budgetTokens, fileContents });

  // 6. In symbol granularity, derive the affected symbols per file so the
  //    assembler renders symbol-scoped sections (§6.1). Empty when the graph
  //    lacks symbol nodes, in which case rendering falls back to file level.
  const affectedSymbols =
    granularity === "symbol"
      ? collectAffectedSymbols(graph, changed, report)
      : undefined;

  // 7. Assemble the pack (contract overlay fields from impact report §8.4).
  return assembleContextPack({
    selection,
    fileContents,
    rankedFiles: ranked,
    budgetTokens,
    taskId,
    ...(affectedSymbols ? { affectedSymbols } : {}),
    affectedValidations: report.affectedValidations,
    affectedAgents: report.affectedAgents,
    affectedWorkflows: report.affectedWorkflows,
  });
}
